// Package imports:
import React, { useState } from 'react';
import { NumberFormatValues } from 'react-number-format';
// Component imports:
import LmdTable from '../../../ui-elements/Table/LmdTable';
import Flag, {FlagNames} from '../../../ui-elements/Flag/Flag';
import ColoredNumber from '../../../ui-elements/ColoredNumber/ColoredNumber';
import Input from '../../../ui-elements/Input/Input';
import Link from '../../../ui-elements/Link/Link';
// Service imports:
import { formatNumber, convertToPercentage, getAPItoday } from '../../../services/utils';
import { useApiLmdData } from '../../../services/apiHooks';
import { currencyFractionDigits } from '../../../services/config';
// Type imports:
import { IDefaultProps } from '../../../types/Types';
import { IApiLmdCurrency } from '../../../types/CurrencyTypes';
import MiniGraph from '../../../ui-elements/MiniGraph/MiniGraph';

const CURRENCIES_TO_SHOW = ['ISK', 'EUR', 'USD', 'GBP', 'JPY', 'CHF', 'DKK', 'SEK', 'NOK', 'CAD', 'PLN'];

export interface ICurrencyInputState {
    value: number | null,
    currency: string | null,
    averagerate: number | null
}

interface IOwnProps {
    basecurrency?: string
}
type Props = IDefaultProps & IOwnProps;

const Islandsbanki: React.FC<Props> = ({
    accessToken,
    refreshRateMs,
    basecurrency = 'ISK'
}) => {
    const [ currencies ] = useApiLmdData<IApiLmdCurrency[]>(
        `/v1/market_data/v1/keldan/base_currency/${basecurrency}/cross_currency/[${CURRENCIES_TO_SHOW.join(';')}]/sources/ISB/rate_types/almenntgengi/currency_rate?date_rate=${getAPItoday()}`,
        accessToken,
        refreshRateMs
    );
    const [ input, setInput ] = useState<ICurrencyInputState | null>({
        value: 1000,
        currency: 'ISK',
        averagerate: 1,
    });
    const [ focusedSymbol, setFocusedSymbol ] = useState<string | null>(null);

    const handleData = () => {
        const { data, error } = currencies;
        if (data === null) {
            return currencies;
        } else {
            return {
                error,
                data: [...data].sort((a, b) => (
                    (a.Crosscurrency === null && b.Crosscurrency === null)
                    ? 0
                    : (a.Crosscurrency === null)
                    ? -1
                    : (b.Crosscurrency === null)
                    ? 1
                    : (CURRENCIES_TO_SHOW.indexOf(a.Crosscurrency) - CURRENCIES_TO_SHOW.indexOf(b.Crosscurrency))
                ))
            }
        }
    };

    return (
        <div className='KCL_islandsbanki'>
            <LmdTable
                apiData={handleData()}
                columns={[{
                    title: 'Mynt',
                    dataAccessor: ({Crosscurrency}) => (
                        <div className="flag-and-symbol">
                            <span className="flag">
                                <a href={`/Markadir/Gjaldmidlar/CBI${Crosscurrency}`} aria-label={`Gjaldmiðlar - ${Crosscurrency}`}>
                                    <Flag name={Crosscurrency as FlagNames} />
                                </a>
                            </span>
                            <Link className="symbol" url={`/Markadir/Gjaldmidlar/CBI${Crosscurrency}`} linkSize='13'>{Crosscurrency}</Link>
                        </div>
                    ),
                    textAlign: 'left',
                    simpleSortable: ({Crosscurrency}) => Crosscurrency
                }, {
                    title: 'Breyting',
                    dataAccessor: ({ChangePer}) => (
                        <ColoredNumber
                            sign={(ChangePer === null) ? 'neutral' : (ChangePer < 0) ? 'neg' : (ChangePer > 0) ? 'pos' : 'neutral'}
                        >
                            {convertToPercentage(ChangePer, true)}
                        </ColoredNumber>
                    ),
                    simpleSortable: ({ChangePer}) => ChangePer
                }, {
                    title: 'Sl. 3 Mán',
                    dataAccessor: ({Crosscurrency}) => (Crosscurrency && Crosscurrency !== 'ISK') && (
                        <MiniGraph
                            symbol={Crosscurrency}
                            folder='Currencies/ISB'
                        />
                    )
                }, {
                    title: 'Kaup',
                    dataAccessor: ({Crosscurrency, Buyrate}) => formatNumber(Buyrate, '-', currencyFractionDigits(Crosscurrency ?? '')),
                    simpleSortable: ({Buyrate}) => Buyrate
                }, {
                    title: 'Sala',
                    dataAccessor: ({Crosscurrency, Sellrate}) => formatNumber(Sellrate, '-', currencyFractionDigits(Crosscurrency ?? '')),
                    simpleSortable: ({Sellrate}) => Sellrate
                }, {
                    title: 'Myntbreyta',
                    dataAccessor: ({Crosscurrency, Averagerate}) => (
                        <Input
                            className='currency_input'
                            inputType="numberFormat"
                            inputSize="sm"
                            textAlign="right"
                            autoComplete="off"
                            value={
                                (input === null)
                                ? undefined // No input, display nothing.
                                : (Crosscurrency === input.currency)
                                ? (input.value ?? undefined) // Inputting currency, show value.
                                : (!Averagerate || (input.value === null) || (input.averagerate === null))
                                ? undefined // Not enough data, display nothing.
                                : (((input.value) * (input.averagerate)) / Averagerate)
                            }
                            thousandSeparator='.'
                            decimalSeparator=','
                            decimalScale={currencyFractionDigits(Crosscurrency ?? '')}
                            fixedDecimalScale={true}
                            allowNegative={false}
                            allowLeadingZeros={false}
                            onClick={e => e.currentTarget.select()}
                            onFocus={() => setFocusedSymbol(Crosscurrency)}
                            onValueChange={(values: NumberFormatValues) => {
                                if (focusedSymbol === Crosscurrency) {
                                    setInput({
                                        value: values.floatValue || null,
                                        currency: Crosscurrency,
                                        averagerate: Averagerate
                                    });
                                }
                            }}
                            aria-label={`${Crosscurrency} gildi`}
                        />
                    )
                }]}
            />
        </div>
    );
}

export default Islandsbanki;